import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Api } from "@/api/Api";
import { VariantFilter } from "@/models/VariantFilter";
import { AdditionalFiltersDto } from "@/models/dto/AdditionalFiltersDto";
import { Propulsion } from "@/models/Propulsion";
import { Helpers } from "@/global-scripts/helpers";
import {AllValidityDomainsDto, ValidityDomains} from "@/settings/ValidityDomains";

@Module({
  name: "filterModule",
})
export default class FilterModule extends VuexModule {
  public static readonly persisted = [
    "FilterModule._market",
    "FilterModule._model",
    "FilterModule._structureWeek",
    "FilterModule._propulsion",
    "FilterModule._electricCapacity",
    "FilterModule._electricPowertrain",
  ];

  private _markets: string[] = [];
  private _models: string[] = [];
  private _structureWeeks: string[] = [];
  private _propulsions: Propulsion[] = [];

  private _market: VariantFilter | undefined = undefined;
  private _model: VariantFilter | undefined = undefined;
  private _structureWeek: VariantFilter | undefined = undefined;
  private _propulsion: VariantFilter | undefined = undefined;
  private _electricCapacity: VariantFilter | undefined = undefined;
  private _electricPowertrain: VariantFilter | undefined = undefined;
  
  private _initialFetchComplete: boolean = false;
  
  private _platform: string = undefined;

  private _onlyShowNews = false;

  private _allValidities: AllValidityDomainsDto | undefined = undefined;

  public get markets(): string[] {
    return this._markets;
  }

  public get market(): VariantFilter | undefined {
    return this.markets.length > 0 ? (this._market as VariantFilter) : undefined;
  }

  public get models(): string[] {
    return this._models;
  }

  public get model(): VariantFilter | undefined {
    return this.models.length > 0 ? (this._model as VariantFilter) : undefined;
  }

  public get platform(): string {
    return this._platform;
  }

  public get structureWeeks(): string[] {
    return this._structureWeeks;
  }

  public get structureWeek(): VariantFilter | undefined {
    return this.structureWeeks.length > 0 ? (this._structureWeek as VariantFilter) : undefined;
  }

  public get propulsions(): string[] {
    if (!this._propulsions) {
      return [];
    }

    return this._propulsions.map((e) => e.propulsion);
  }

  public get propulsion(): VariantFilter | undefined {
    return this.propulsions.length > 0 ? (this._propulsion as VariantFilter) : undefined;
  }

  public get gearboxes(): VariantFilter<string[]> | undefined {
    if (!this.propulsion) {
      return undefined;
    }

    const propulsion = this.propulsion as VariantFilter;
    const propulsionProp = this._propulsions.find((e) => e.propulsion === propulsion.entry);

    return propulsionProp
      ? new VariantFilter(ValidityDomains.gearboxes, propulsionProp.gearboxes)
      : undefined;
  }

  public get electricCapacities(): string[] {
    if (!this.propulsion) {
      return [];
    }

    const propulsion = this.propulsion as VariantFilter;
    const propulsionProp = this._propulsions.find((e) => e.propulsion === propulsion.entry);

    return propulsionProp ? propulsionProp.electricCapacity.map((e) => e.electricCapacity) : [];
  }

  public get electricCapacity(): VariantFilter | undefined {
    return this.electricCapacities.length > 0 ? (this._electricCapacity as VariantFilter) : undefined;
  }

  public get electricPowertrains(): string[] {
    if (!this.electricCapacity) {
      return [];
    }

    const propulsion = this.propulsion as VariantFilter;
    const propulsionProp = this._propulsions.find((e) => e.propulsion === propulsion.entry);

    const electricCapacityProp = propulsionProp.electricCapacity.find(
      (e) => e.electricCapacity === this.electricCapacity.entry
    );
    return electricCapacityProp ? electricCapacityProp.electricPowertrain : [];
  }

  public get electricPowertrain(): VariantFilter | undefined {
    return this.electricPowertrains.length > 0 ? (this._electricPowertrain as VariantFilter) : undefined;
  }

  public get allValidities(): AllValidityDomainsDto {
    return this._allValidities;
  }
  
  public get initialFetchComplete(): boolean {
    return this._initialFetchComplete;
  }

  public get activeFilters(): VariantFilter<string | string[]>[] {
    const activeFilters: VariantFilter<string | string[]>[] = [];

    if (this.market) {
      activeFilters.push(this.market);
    }
    if (this.model) {
      activeFilters.push(this.model);
    }
    if (this.structureWeek) {
      activeFilters.push(this.structureWeek);
    }
    if (this.propulsion) {
      activeFilters.push(this.propulsion);
    }
    if (this.electricCapacity) {
      activeFilters.push(this.electricCapacity);
    }
    if (this.electricPowertrain) {
      activeFilters.push(this.electricPowertrain);
    }
    if (this.gearboxes) {
      activeFilters.push(this.gearboxes);
    }

    return activeFilters;
  }

  public get allFiltersSet(): boolean {
    let allFilterSet =
      this.market !== undefined &&
      this.model !== undefined &&
      this.structureWeek !== undefined &&
      this.propulsion !== undefined;

    // If currently selected propulsion enables the additional filters
    if (this.electricCapacities.length > 0) {
      allFilterSet = allFilterSet && this.electricCapacity !== undefined && this.electricPowertrain !== undefined;
    }

    return allFilterSet;
  }

  public get onlyShowNews(): boolean {
    return this._onlyShowNews;
  }

  public get basePath(): string {
    if (!this.allFiltersSet) {
      return "";
    }

    const market = this.market as VariantFilter;
    const model = this.model as VariantFilter;
    const structureWeek = this.structureWeek as VariantFilter;
    const propulsion = this.propulsion as VariantFilter;

    const parts = [
      Helpers.urlFriendlyName(this.context.rootGetters.locale),
      Helpers.urlFriendlyName(market.entry),
      Helpers.urlFriendlyName(model.entry),
      structureWeek.entry.toLowerCase(),
      Helpers.urlFriendlyName(propulsion.entry),
    ];

    if (this.electricCapacity && this.electricPowertrain) {
      parts.push(Helpers.urlFriendlyName(this.electricCapacity.entry));
      parts.push(Helpers.urlFriendlyName(this.electricPowertrain.entry));
    }

    return `/${parts.join("/")}/cat`;
  }

  @Mutation
  setMarkets(markets: string[]): void {
    this._markets = markets;
  }

  @Mutation
  setMarket(market: VariantFilter | undefined): void {
    this._market = market;
  }

  @Mutation
  setModels(models: string[]): void {
    this._models = models;
  }

  @Mutation
  setModel(model: VariantFilter | undefined): void {
    this._model = model;
  }

  @Mutation
  setStructureWeeks(structureWeeks: string[]): void {
    this._structureWeeks = structureWeeks;
  }

  @Mutation
  setStructureWeek(structureWeek: VariantFilter | undefined): void {
    this._structureWeek = structureWeek;
  }

  @Mutation
  setPropulsion(propulsion: VariantFilter | undefined): void {
    this._propulsion = propulsion;
  }

  @Mutation
  setElectricPowertrain(electricPowerTrain: VariantFilter | undefined): void {
    this._electricPowertrain = electricPowerTrain;
  }

  @Mutation
  setElectricCapacity(electricCapacity: VariantFilter | undefined): void {
    this._electricCapacity = electricCapacity;
  }

  @Mutation
  setAdditionalFilters(additionalFilters: AdditionalFiltersDto | undefined): void {
    if (additionalFilters) {
      this._propulsions = additionalFilters.propulsions;
      this._platform = additionalFilters.platform;
    } else {
      this._propulsions = [];
      this._platform = undefined;
    }
  }

  @Mutation
  setAllValidities(validities: AllValidityDomainsDto): void {
    this._allValidities = validities;
  }

  @Mutation
  setInitialFetchComplete(value: boolean): void {
    this._initialFetchComplete = value;
  }

  @Mutation
  resetFilters(domain: string): void {
    // Fall through to next case to reset the filters that are dependent on the previous filters
    /* eslint-disable no-fallthrough */
    switch (domain) {
      case ValidityDomains.market:
        if (this._market) {
          this._market = undefined;
        }
      case ValidityDomains.model:
        if (this._model) {
          this._model = undefined;
        }
      case ValidityDomains.structureWeek:
        if (this._structureWeek) {
          this._structureWeek = undefined;
        }
      case ValidityDomains.propulsion:
        if (this._propulsion) {
          this._propulsion = undefined;
        }
      case ValidityDomains.electricCapacity:
        if (this._electricCapacity) {
          this._electricCapacity = undefined;
        }
      case ValidityDomains.electricPowertrain:
        if (this._electricPowertrain) {
          this._electricPowertrain = undefined;
        }
    }
    /* eslint-enable */
  }

  @Mutation
  setOnlyShowNews(value: boolean): void {
    this._onlyShowNews = value;
  }

  @Action({ commit: "setMarkets", rawError: true })
  async fetchMarkets(): Promise<string[]> {
    return await Api.loadMarkets();
  }

  @Action({ commit: "setModels", rawError: true })
  async fetchModels(): Promise<string[]> {
    if (!this.market) {
      throw "Unable to fetch models. Make sure to set 'Market'";
    }

    return await Api.loadModels(this.market.entry);
  }

  @Action({ commit: "setStructureWeeks", rawError: true })
  async fetchStructureWeeks(): Promise<string[]> {
    if (!this.market || !this.model) {
      throw "Unable to fetch structure weeks. Make sure to set 'Market' and 'Model'";
    }

    return await Api.loadStructureWeeks(this.market.entry, this.model.entry);
  }

  @Action({ commit: "setAdditionalFilters", rawError: true })
  async fetchAdditionalFilters(): Promise<AdditionalFiltersDto> {
    if (!this.market || !this.model || !this.structureWeek) {
      throw "Unable to fetch structure weeks. Make sure to set 'Market', 'Model' and 'StructureWeek'";
    }

    return await Api.loadAdditionalFilters(this.market.entry, this.model.entry, this.structureWeek.entry);
  }

  @Action({ commit: "setAllValidities", rawError: true })
  async fetchAllValidities(): Promise<AllValidityDomainsDto> {
    return await Api.loadValidities();
  }
}
